import { RoyaltyReportResponse } from "../../../interface/api/RoyaltyReportResponse";
import { RoyaltyReportPayload } from "../../../interface/api/RoyaltyReportPayload";
import { ReportRunResult } from "../../../interface/ReportRunResult";
import { ReportRunSection } from "../../../interface/ReportRunSection";
import { ReportRunTone } from "../../../interface/ReportRunTone";
import { formatDate } from "../../../utils/date-helper";
import { formatCurrency } from "../../../utils/parser";
import { royaltyStreamLabel } from "../../../utils/constants";

const count = (value: number) => value.toLocaleString();

/**
 * Everything the royalty run decided, written out. The total on its own has never been
 * checkable: it depends on which tickets were treated as real, which were left unpriced
 * and — now — which were valued at an average rather than a recorded price. Each of
 * those is reported as its own number so the bottom line can be reconciled against them.
 */
export const buildRoyaltySummary = (
  report: RoyaltyReportResponse,
  payload: RoyaltyReportPayload
): ReportRunResult => {
  const period = `${formatDate(payload.fromDate, false)} – ${formatDate(
    payload.toDate,
    false
  )}`;

  // Counted whether or not the run listed them: an unpriced ticket nobody can see is
  // how a fifth of July stayed invisible for months.
  const stillUnpriced = report.unpricedTicketCount - report.estimatedTicketCount;

  const notes: string[] = [];

  if (report.estimatedTicketCount > 0) {
    notes.push(
      `${count(report.estimatedTicketCount)} tickets had no selling price and were valued at ` +
        (report.averageSalePrice != null
          ? `${formatCurrency(report.averageSalePrice)}, the average of the ` +
            `${count(report.pricedTicketCount)} priced tickets in this period. They add `
          : "the average of the priced tickets in their own stream and period. They add ") +
        `${formatCurrency(report.estimatedRoyalty)}, bringing the combined total to ` +
        `${formatCurrency(report.totalRoyaltyWithEstimates)}.`
    );
  } else if (report.unpricedTicketCount > 0) {
    const listed =
      report.missingSalePriceCount > 0
        ? "They are listed at $0.00 and are in no total"
        : 'They are not listed at all — tick "Include tickets with no selling price" to see which ones';

    notes.push(
      `${count(report.unpricedTicketCount)} tickets have no selling price. ${listed}. ` +
        `Tick "Estimate missing selling prices" to value them at the period average instead.`
    );
  }

  if (report.unusedTicketCount > 0) {
    notes.push(
      `${count(report.unusedTicketCount)} of the ${count(report.ticketsInRange)} tickets ` +
        `marked received in this range were never written up, so they were skipped rather ` +
        `than billed.`
    );
  }

  if (report.emailError) {
    notes.push(`The email did not go out: ${report.emailError}`);
  }

  const sections: ReportRunSection[] = [
    {
      title: "What was run",
      facts: [
        { label: "Partner", value: report.partnerName },
        { label: "Stream", value: royaltyStreamLabel(payload.stream) },
        { label: "Period", value: period },
        {
          label: "Partner share",
          value:
            `${(report.partnerRate * 100).toFixed(0)}% + ` +
            `${formatCurrency(report.partnerBaseRoyalty)}` +
            `${report.partnerAppliesDeficit ? "" : ", deficit-exempt"}`,
        },
        { label: "Current CPI", value: payload.currentCPI.toFixed(1) },
        { label: "Town rate", value: formatCurrency(payload.townRate) },
        {
          label: "Adjusted ticket price",
          value: formatCurrency(report.adjustedTicketPrice),
        },
        ...(report.netTicketPrice != null
          ? [
              {
                label: "Net ticket price (Hydrovac / SQ)",
                value: formatCurrency(report.netTicketPrice),
              },
            ]
          : []),
      ],
    },
    {
      // Loads and royalty per stream, then the grand total, as the workbook opens with.
      title: "By stream",
      facts: [
        ...report.streams.map((s) => ({
          label: s.label,
          value:
            `${count(s.settledTicketCount)} loads · ` +
            formatCurrency(s.totalRoyaltyWithEstimates),
        })),
        ...(report.streams.length > 1
          ? [
              {
                label: "Grand total",
                value:
                  `${count(report.settledTicketCount)} loads · ` +
                  formatCurrency(report.totalRoyaltyWithEstimates),
                tone: (report.totalRoyaltyWithEstimates >= 0
                  ? "positive"
                  : "negative") as ReportRunTone,
              },
            ]
          : []),
      ],
    },
    {
      title: "Tickets",
      facts: [
        {
          label: "Received in range",
          value: count(report.ticketsInRange),
        },
        {
          label: "Unused, skipped",
          value: count(report.unusedTicketCount),
          tone: report.unusedTicketCount > 0 ? "warning" : "default",
        },
        { label: "On the report", value: count(report.ticketCount) },
        {
          label: "Priced",
          value: count(report.pricedTicketCount),
        },
        {
          label: "Estimated",
          value: count(report.estimatedTicketCount),
          tone: report.estimatedTicketCount > 0 ? "warning" : "default",
        },
        {
          label: "Still unpriced",
          value: count(stillUnpriced),
          tone: stillUnpriced > 0 ? "warning" : "positive",
        },
      ],
    },
    {
      title: "Royalty",
      facts: [
        {
          label: "From priced tickets",
          value: formatCurrency(report.totalRoyalty),
          tone: report.totalRoyalty >= 0 ? "positive" : "negative",
        },
        {
          label: "From estimates",
          value: formatCurrency(report.estimatedRoyalty),
          tone: report.estimatedTicketCount > 0 ? "warning" : "default",
        },
        {
          label: "Combined total",
          value: formatCurrency(report.totalRoyaltyWithEstimates),
          tone: report.totalRoyaltyWithEstimates >= 0 ? "positive" : "negative",
        },
        {
          label: "Average selling price",
          value:
            report.averageSalePrice != null
              ? formatCurrency(report.averageSalePrice)
              : "—",
        },
      ],
    },
  ];

  if (report.ticketCount === 0) {
    return {
      status: "empty",
      headline: "No tickets settled in this period.",
      note: notes.join(" "),
      sections,
    };
  }

  return {
    status: "success",
    headline:
      `${count(report.pricedTicketCount)} priced tickets settled at ` +
      `${formatCurrency(report.totalRoyalty)}.`,
    note: notes.join(" "),
    sections,
  };
};
